/**
 * "Link by contract email" - the way in for a customer whose Google address is
 * unknown to us, but who holds a policy on a different address.
 *
 * Both endpoints are unauthenticated on purpose: the customer has just been
 * refused a sign-in, so they hold no token.
 */

/**
 * The body of `POST /link/contract/request`.
 *
 * `lang` is the portal's current language, and it decides the language of the
 * confirmation email. It is the same locale the sign-up and password-reset
 * calls send to Cognito as `clientMetadata.locale` - see `AuthLocaleService`.
 * The backend normalises it and falls back to German, so an unexpected value
 * cannot break the call. `POST /link/contract/verify` takes no language: it
 * sends no mail.
 */
export interface ContractLinkRequestBody {
  email: string;
  lang: string;
}

/**
 * `POST /link/contract/request` answers the same for an address we know and one
 * we have never seen. The body carries no information the UI may branch on -
 * see `ContractLinkStep` and the comment on the request step of the modal.
 */
export interface ContractLinkRequestResponse {
  sent: boolean;
}

/**
 * `POST /link/contract/verify` answers with the Google consent URL to send the
 * browser to. `expiresIn` is seconds, and is not shown - the URL outlives any
 * plausible stay on the step.
 */
export interface ContractLinkVerifyResponse {
  authUrl: string;
  expiresIn?: number;
}

/** The steps of the modal, in the order the customer walks through them. */
export type ContractLinkStep = 'email' | 'code' | 'redirect';

/**
 * What the code step can tell the customer.
 *
 * `invalid-code` covers a wrong code, an expired code, and an address with no
 * contract. The backend merges the three on purpose, and so does the copy: any
 * difference here would say whether an address holds a policy.
 */
export type ContractLinkCodeError = 'invalid-code' | 'too-many-attempts' | 'generic';
